import { ChartOptions } from "chart.js";
import { numberAfterDotCounter } from "helpers/numberAfterDotCounter";
import { theme } from "styling/theme";

const getOrCreateTooltip = (chart: any) => {
    let tooltipEl = chart.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.style.background = "rgba(255, 255, 255, 1)";
        tooltipEl.style.borderRadius = "3px";
        tooltipEl.style.color = "rgba(51, 51, 51, 1)";
        tooltipEl.style.boxShadow = "0px 4px 10px rgba(110, 102, 102, 0.2)";
        tooltipEl.style.opacity = "1";
        tooltipEl.style.pointerEvents = "none";
        tooltipEl.style.position = "absolute";
        tooltipEl.style.zIndex = 9999;
        tooltipEl.style.transform = "translate(-50%, 0)";
        tooltipEl.style.transition = "all .1s ease";

        const mainDiv = document.createElement("div");
        mainDiv.style.margin = "0px";

        tooltipEl.appendChild(mainDiv);
        chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
};

export const externalTooltipHandler = (context: any) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = "0";
        return;
    }
    // chart.canvas;

    // Set Text
    if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map((b: any) => b.lines);

        const titleDiv = document.createElement("div");

        titleLines.forEach((title: string) => {
            const titleSpan = document.createElement("span");
            titleSpan.style.color = "rgba(51, 51, 51, 1)";
            const text = document.createTextNode(title);

            const titleLine = document.createElement("div");
            titleLine.style.borderBottom = "1px solid #E8E8E8";
            titleLine.style.marginBottom = "10px";
            titleLine.style.marginTop = "10px";

            titleSpan.appendChild(text);
            titleDiv.appendChild(titleSpan);
            titleDiv.appendChild(titleLine);
        });

        const bodyDiv = document.createElement("div");
        bodyLines.forEach((body: string, i: number) => {
            const colors = tooltip.labelColors[i];

            const colorLine = document.createElement("span");
            colorLine.style.background = colors.backgroundColor;
            colorLine.style.borderColor = colors.borderColor;
            colorLine.style.borderWidth = "2px";
            colorLine.style.marginRight = "4px";
            colorLine.style.marginBottom = "4px";
            colorLine.style.height = "2px";
            colorLine.style.width = "20px";
            colorLine.style.display = "inline-block";

            const temperatureSpan = document.createElement("div");
            temperatureSpan.style.paddingLeft = "24px";
            temperatureSpan.style.fontWeight = "600";

            const monitorId = body.toString().split(":")[0].replace(":", " ");
            const temperature = body.toString().split(":")[1].replace(":", " ");

            const IdText = document.createTextNode(monitorId);
            const br = document.createElement("br");
            const DataText = document.createTextNode(
                numberAfterDotCounter(Number.parseFloat(temperature)).toString() + "°C"
            );

            bodyDiv.appendChild(colorLine);
            bodyDiv.appendChild(br);
            bodyDiv.appendChild(IdText);
            temperatureSpan.appendChild(DataText);
            bodyDiv.appendChild(temperatureSpan);
            bodyDiv.appendChild(br);
        });

        const tableRoot = tooltipEl.querySelector("div");

        // Remove old children
        while (tableRoot!.firstChild) {
            tableRoot!.firstChild.remove();
        }

        tableRoot!.appendChild(titleDiv);
        tableRoot!.appendChild(bodyDiv);
    }

    // const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    const canvasPosition = chart.canvas.getBoundingClientRect();

    const left = canvasPosition.left + tooltip.caretX + window.pageXOffset;

    // const top = canvasPosition.top + window.pageYOffset - 40; //old
    const top = canvasPosition.top + window.pageYOffset + tooltip.caretY;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = left + "px";
    tooltipEl.style.top = top + "px";
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

export const createOptions = (
    minDate: number,
    maxDate: number,
    temperatureLowerLimit: number,
    temperatureUpperLimit: number
) => {
    const options: ChartOptions<"line"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: false,
            },
            legend: {
                display: false,
                position: "bottom",
                maxHeight: 40,
                labels: {
                    boxWidth: 20,
                    boxHeight: 1,
                },
            },
            tooltip: {
                enabled: false,
                mode: "index",
                intersect: false,
                external: externalTooltipHandler,
            },
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    drag: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true,
                    },
                    mode: "xy",
                },
                pan: {
                    enabled: true,
                    mode: "xy",
                    modifierKey: "ctrl",
                },
                limits: {
                    x: {
                        min: minDate,
                        max: maxDate,
                    },
                    y: {
                        min: -100,
                        max: 100,
                    },
                },
            },
            annotation: {
                annotations: {
                    box1: {
                        type: "box",
                        xMin: minDate,
                        xMax: maxDate,
                        yMin: temperatureLowerLimit,
                        yMax: temperatureUpperLimit,
                        backgroundColor: "rgba(255, 99, 132, 0)",
                        borderColor: "#270ADC",
                    },
                },
            },
        },
        elements: {
            point: {
                radius: 0,
            },
        },
        interaction: {
            intersect: false,
            mode: "index",
        },
        scales: {
            x: {
                type: "time",
                min: minDate,
                max: maxDate,
                time: {
                    minUnit: "hour",
                },
                ticks: {
                    autoSkip: true,
                    autoSkipPadding: 20,
                    maxRotation: 0,
                    color: theme.colors.neutrals.neutral300,
                },
                grid: {
                    color: theme.colors.neutrals.neutral200,
                    tickColor: "transparent",
                },
            },
            y: {
                type: "linear",
                suggestedMin: 0,
                ticks: {
                    stepSize: 1,
                    color: theme.colors.neutrals.neutral300,
                },
                grid: {
                    tickColor: "transparent",
                    color: theme.colors.neutrals.neutral200,
                },
            },
        },
    };
    return options;
};
